import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSimplifiedAuth } from '../../hooks/useSimplifiedAuth';
import SmartLocationCapture from '../location/SmartLocationCapture';

// Checkout flow states
type CheckoutState = 'auth' | 'payment' | 'location' | 'processing' | 'complete' | 'error';

interface OneClickCheckoutProps {
  isVisible: boolean;
  onClose: () => void;
  initialPlan?: 'monthly' | 'yearly';
  initialAddons?: string[];
  onSuccess?: () => void;
}

// Plan configurations
const PLANS = {
  monthly: { 
    price: 9, 
    displayPrice: '$9/month',
    priceId: 'price_monthly_2024',
    trialDays: 7 
  },
  yearly: { 
    price: 89, 
    displayPrice: '$89/year',
    priceId: 'price_yearly_2024',
    trialDays: 14 
  }
};

const ADDONS = {
  starlink: { 
    price: 5, 
    displayPrice: '+$5/month',
    name: 'Starlink Tracking',
    description: 'Real-time Starlink satellite constellation tracking'
  }
};

// Animations
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const Overlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const Modal = styled.div<{ $isVisible: boolean }>`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 20px;
  border: 2px solid rgba(139, 92, 246, 0.3);
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${props => props.$isVisible ? slideUp : 'none'} 0.4s ease-out;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: white;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  padding: 32px 32px 24px;
  text-align: center;
  border-bottom: 1px solid rgba(139, 92, 246, 0.2);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 1.5rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  background: linear-gradient(45deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  margin: 0;
`;

const Content = styled.div`
  padding: 32px;
`;

const StateContent = styled.div<{ $state: CheckoutState }>`
  opacity: ${props => props.$state ? 1 : 0};
  animation: ${fadeIn} 0.4s ease-in-out;
`;

const PlanSelector = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const PlanCard = styled.button<{ $selected: boolean }>`
  flex: 1;
  padding: 20px;
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.$selected 
    ? 'transparent'
    : 'rgba(139, 92, 246, 0.3)'
  };
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  
  &:hover {
    transform: translateY(-2px);
    border-color: rgba(139, 92, 246, 0.6);
  }
`;

const PlanName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 4px;
`;

const PlanPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 4px;
`;

const PlanTrial = styled.div`
  font-size: 0.85rem;
  opacity: 0.8;
`;

const AddonCard = styled.label<{ $checked: boolean }>`
  display: block;
  padding: 16px;
  background: ${props => props.$checked 
    ? 'rgba(139, 92, 246, 0.2)'
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.$checked 
    ? 'rgba(139, 92, 246, 0.6)'
    : 'rgba(139, 92, 246, 0.2)'
  };
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  
  &:hover {
    border-color: rgba(139, 92, 246, 0.6);
  }
`;

const AddonHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 4px;
`;

const AddonName = styled.span`
  font-weight: 600;
  flex: 1;
`;

const AddonPrice = styled.span`
  font-weight: 700;
  color: #8b5cf6;
`;

const AddonDescription = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const CheckoutButton = styled.button<{ $loading?: boolean }>`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.$loading ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$loading ? 0.7 : 1};
  transition: all 0.3s ease;
  margin-top: 24px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ProcessingState = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(139, 92, 246, 0.3);
  border-top: 4px solid #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ProcessingText = styled.div`
  color: white;
  font-size: 1.1rem;
  margin-bottom: 8px;
`;

const ProcessingSubtext = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
`;

const SuccessState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: white;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 16px;
  animation: ${pulse} 1s ease-in-out;
`;

const OneClickCheckout: React.FC<OneClickCheckoutProps> = ({
  isVisible,
  onClose,
  initialPlan = 'monthly',
  initialAddons = [],
  onSuccess
}) => {
  const { user, firebaseUser, authState, userFlow, signInWithGoogle, error: authError } = useSimplifiedAuth();
  
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('auth');
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>(initialPlan);
  const [selectedAddons, setSelectedAddons] = useState<string[]>(initialAddons);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      setCheckoutState(authState === 'authenticated' ? 'payment' : 'auth');
      setError(null);
    }
  }, [isVisible, authState]);

  // Handle authentication step
  const handleAuth = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      await signInWithGoogle();
      setCheckoutState('payment');
    } catch (error) {
      console.error('Auth failed:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment step
  const handlePayment = async () => {
    // Check if user flow requires location first
    if (userFlow === 'new_user' || (user && !user.setup.locationComplete)) {
      setCheckoutState('location');
      return;
    }
    
    // Proceed with payment
    await processPayment();
  };

  // Process payment (split from handlePayment for location flow)
  const processPayment = async () => {
    try {
      setIsProcessing(true);
      setCheckoutState('processing');
      setError(null);

      const plan = PLANS[selectedPlan];
      const addons = selectedAddons.map(id => ADDONS[id as keyof typeof ADDONS]);
      
      const totalPrice = plan.price + addons.reduce((sum, addon) => sum + addon.price, 0);

      // Create checkout session
      const idToken = await firebaseUser?.getIdToken(true); // Get fresh JWT token
      
      const response = await fetch('https://spacepings.space/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}` // Use proper JWT token instead of UID
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          addons: selectedAddons,
          userFlow,
          successUrl: `${window.location.origin}/dashboard?checkout=success`,
          cancelUrl: `${window.location.origin}/pricing?checkout=cancelled`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
      
    } catch (error) {
      console.error('Payment failed:', error);
      setError('Payment setup failed. Please try again.');
      setCheckoutState('payment');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle location completion
  const handleLocationCaptured = async (locationData: any) => {
    console.log('Location captured:', locationData);
    // Continue to payment after location is set
    setTimeout(() => {
      processPayment();
    }, 1500); // Brief pause to show success
  };

  // Handle location skip
  const handleLocationSkip = () => {
    // Continue to payment without location
    processPayment();
  };

  // Calculate total price
  const calculateTotal = () => {
    const plan = PLANS[selectedPlan];
    const addons = selectedAddons.map(id => ADDONS[id as keyof typeof ADDONS]);
    return plan.price + addons.reduce((sum, addon) => sum + addon.price, 0);
  };

  const getButtonText = () => {
    if (checkoutState === 'auth') {
      return isProcessing ? 'Signing in...' : 'Continue with Google';
    }
    
    if (checkoutState === 'payment') {
      const total = calculateTotal();
      const plan = PLANS[selectedPlan];
      return isProcessing 
        ? 'Processing...' 
        : `Start ${plan.trialDays}-Day Free Trial`;
    }
    
    return 'Continue';
  };

  if (!isVisible) return null;

  return (
    <Overlay $isVisible={isVisible} onClick={onClose}>
      <Modal $isVisible={isVisible} onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        
        <Header>
          <Logo>🚀 SpacePings</Logo>
          <Title>
            {checkoutState === 'auth' && 'Join SpacePings'}
            {checkoutState === 'payment' && 'Choose Your Plan'}
            {checkoutState === 'location' && 'Personalize Your Experience'}
            {checkoutState === 'processing' && 'Setting Up Your Account'}
            {checkoutState === 'complete' && 'Welcome to SpacePings!'}
          </Title>
          <Subtitle>
            {checkoutState === 'auth' && 'Sign in to start your space exploration journey'}
            {checkoutState === 'payment' && 'Real-time satellite tracking with free trial'}
            {checkoutState === 'location' && 'Get satellite predictions and alerts for your exact location'}
            {checkoutState === 'processing' && 'Just a moment while we prepare everything...'}
            {checkoutState === 'complete' && 'Your account is ready. Let\'s explore the cosmos!'}
          </Subtitle>
        </Header>

        <Content>
          {/* Authentication State */}
          {checkoutState === 'auth' && (
            <StateContent $state={checkoutState}>
              <CheckoutButton 
                onClick={handleAuth}
                disabled={isProcessing}
                $loading={isProcessing}
              >
                {getButtonText()}
              </CheckoutButton>
              {error && (
                <div style={{ color: '#ef4444', textAlign: 'center', marginTop: '16px', fontSize: '0.9rem' }}>
                  {error}
                </div>
              )}
            </StateContent>
          )}

          {/* Payment State */}
          {checkoutState === 'payment' && (
            <StateContent $state={checkoutState}>
              <PlanSelector>
                <PlanCard 
                  $selected={selectedPlan === 'monthly'}
                  onClick={() => setSelectedPlan('monthly')}
                >
                  <PlanName>Monthly</PlanName>
                  <PlanPrice>$9/month</PlanPrice>
                  <PlanTrial>7-day free trial</PlanTrial>
                </PlanCard>
                <PlanCard 
                  $selected={selectedPlan === 'yearly'}
                  onClick={() => setSelectedPlan('yearly')}
                >
                  <PlanName>Yearly</PlanName>
                  <PlanPrice>$89/year</PlanPrice>
                  <PlanTrial>14-day free trial</PlanTrial>
                </PlanCard>
              </PlanSelector>

              <AddonCard 
                $checked={selectedAddons.includes('starlink')}
                onClick={() => {
                  setSelectedAddons(prev => 
                    prev.includes('starlink')
                      ? prev.filter(id => id !== 'starlink')
                      : [...prev, 'starlink']
                  );
                }}
              >
                <AddonHeader>
                  <AddonName>Starlink Tracking</AddonName>
                  <AddonPrice>+$5/month</AddonPrice>
                </AddonHeader>
                <AddonDescription>
                  Real-time Starlink satellite constellation tracking
                </AddonDescription>
              </AddonCard>

              <CheckoutButton 
                onClick={handlePayment}
                disabled={isProcessing}
                $loading={isProcessing}
              >
                {getButtonText()}
              </CheckoutButton>

              {error && (
                <div style={{ color: '#ef4444', textAlign: 'center', marginTop: '16px', fontSize: '0.9rem' }}>
                  {error}
                </div>
              )}
            </StateContent>
          )}

          {/* Location State */}
          {checkoutState === 'location' && (
            <StateContent $state={checkoutState}>
              <SmartLocationCapture
                context="auth_completion"
                onLocationCaptured={handleLocationCaptured}
                onSkip={handleLocationSkip}
                showSkipOption={true}
                title="Complete Your Space Setup"
                subtitle="Get personalized satellite predictions and launch alerts for your location"
              />
            </StateContent>
          )}

          {/* Processing State */}
          {checkoutState === 'processing' && (
            <ProcessingState>
              <Spinner />
              <ProcessingText>Launching your space mission...</ProcessingText>
              <ProcessingSubtext>Setting up your personalized dashboard</ProcessingSubtext>
            </ProcessingState>
          )}

          {/* Success State */}
          {checkoutState === 'complete' && (
            <SuccessState>
              <SuccessIcon>🎉</SuccessIcon>
              <ProcessingText>Account created successfully!</ProcessingText>
              <ProcessingSubtext>Redirecting to your mission control...</ProcessingSubtext>
            </SuccessState>
          )}
        </Content>
      </Modal>
    </Overlay>
  );
};

export default OneClickCheckout;